/**
 * Onboarding journey shared types + helpers (M27 PR 1).
 *
 * Mirrors data/models/onboarding_state.py — the JSONB shape the
 * backend stores in customer_groups.onboarding_state.
 */

export type StepStatus =
  | "not_started"
  | "in_progress"
  | "completed"
  | "skipped";

// The 9 canonical step slugs — these are the URL slugs under
// /onboarding/{slug} AND the keys in the JSONB.
export const STEP_SLUGS = [
  "welcome",
  "company",
  "policies",
  "jobs-grades",
  "employees",
  "approvals",
  "integrations",
  "shadow-payroll",
  "go-live",
] as const;
export type StepSlug = (typeof STEP_SLUGS)[number];

/** TanStack Query key for GET/PUT /api/onboarding/state (M27 journey). */
export const ONBOARDING_JOURNEY_QUERY_KEY = [
  "onboarding-journey-state",
] as const;

/** Legacy M7 /api/setup/state — do not share with the journey key. */
export const SETUP_WIZARD_QUERY_KEY = ["setup-wizard-state"] as const;

// Step labels (English) — pages render via i18n in practice; this is
// the fallback when a translation key is missing.
export const STEP_LABELS_EN: Record<StepSlug, string> = {
  welcome: "Welcome",
  company: "Company Structure",
  policies: "Customize Policies",
  "jobs-grades": "Jobs & Grades",
  employees: "Import Employees",
  approvals: "Approval Chains",
  integrations: "Connect Integrations",
  "shadow-payroll": "Shadow Payroll",
  "go-live": "Go Live",
};

export const STEP_EST_MINUTES: Record<StepSlug, number> = {
  welcome: 2,
  company: 10,
  policies: 90,
  "jobs-grades": 30,
  employees: 60,
  approvals: 10,
  integrations: 10,
  "shadow-payroll": 30,
  "go-live": 5,
};

export const STEP_DESCRIPTIONS: Record<StepSlug, string> = {
  welcome: "Meet Solvait and set your AI's autonomy mode.",
  company:
    "Confirm your customer group and add Legal Entities, branches, and departments.",
  policies:
    "Upload your HR policy PDFs — AI extracts grades, leave types, allowances, and flags any conflicts with KSA Labor Law.",
  "jobs-grades":
    "Define grade structure, build the job catalog, and add key positions for your top org chart.",
  employees:
    "Import your team from CSV, Excel, Qiwa contracts, or mixed folders — AI extracts and auto-creates positions.",
  approvals:
    "Configure approval chains for leave, loans, excuses, and end-of-service workflows.",
  integrations:
    "Connect Twilio for WhatsApp Concierge, plus optional Microsoft Teams and government services.",
  "shadow-payroll":
    "Compare Solvait's payroll output side-by-side with your old system before going live.",
  "go-live":
    "Activate live workflows and let Solvait introduce itself to your employees on WhatsApp.",
};

// Steps that cannot be skipped (per spec).
export const NON_SKIPPABLE: ReadonlySet<StepSlug> = new Set<StepSlug>([
  "welcome",
  "go-live",
]);

export interface StepState {
  status: StepStatus;
  last_activity_at: string | null;
  completed_at: string | null;
  skipped_at: string | null;
  skipped_reason?: string | null;
  data: Record<string, unknown>;
}

export interface OnboardingState {
  started_at: string | null;
  current_step: StepSlug | null;
  go_live_at: string | null;
  dismissed_until: string | null;
  steps: Record<StepSlug, StepState>;
}

export function percentComplete(state: OnboardingState | undefined): number {
  if (!state || !state.steps) return 0;
  const total = STEP_SLUGS.length;
  const done = STEP_SLUGS.filter((s) => {
    const st = state.steps[s];
    return st && (st.status === "completed" || st.status === "skipped");
  }).length;
  return Math.round((100 * done) / total);
}

export function isComplete(state: OnboardingState | undefined): boolean {
  return !!state?.go_live_at;
}

export function nextActiveStep(state: OnboardingState | undefined): StepSlug {
  if (!state) return "welcome";
  if (state.current_step) return state.current_step;
  for (const s of STEP_SLUGS) {
    const st = state.steps?.[s];
    if (!st || st.status === "not_started" || st.status === "in_progress") {
      return s;
    }
  }
  return "welcome";
}

export function isDismissed(
  state: OnboardingState | undefined,
  now: Date = new Date(),
): boolean {
  if (!state?.dismissed_until) return false;
  return new Date(state.dismissed_until).getTime() > now.getTime();
}
